'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import logTripPlannerCTAClicked from '@alltrails/amplitude/events/logTripPlannerCTAClicked';
import logTripPlannerCTAViewed from '@alltrails/amplitude/events/logTripPlannerCTAViewed';
import Typography from '@alltrails/core/components/Typography';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import IconTile from '@alltrails/denali/components/IconTile';
import LinkOrNativeAnchor from '@alltrails/denali/components/LinkOrNativeAnchor';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Chevron from '@alltrails/denali/icons/Chevron';
import SparkleFilled from '@alltrails/denali/icons/SparkleFilled';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import {
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  latParamKey,
  lngParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import { getTripPlannerEntryParams, type TripPlannerEntryPoint } from '../../utils/tripPlannerEntryParams';
import type { PlanATripMapBounds, TripPlannerSearchArea } from '../../utils/tripPlannerSearchArea';
import styles from './PlanATripCard.module.scss';
import illustrationSrc from './trip-assistant-illustration.svg';

export type { PlanATripMapBounds };

type PlanATripCardProps = TripPlannerSearchArea & {
  // required when navigating across apps - the trip planner lives in the explore app.
  isNativeLink?: boolean;
  /** Legacy-only: the larger card styling used on location pages before the Entrypoints 2.0 restyle. */
  variant?: 'default' | 'prominent';
  className?: string;
  entryPoint: TripPlannerEntryPoint;
  locationId?: number;
  parkId?: number;
};

const getTripPlannerQuery = ({
  entryPoint,
  locationId,
  mapBounds,
  mapCenter,
  parkId
}: Pick<PlanATripCardProps, 'entryPoint' | 'locationId' | 'mapBounds' | 'mapCenter' | 'parkId'>) => {
  const entryParams = getTripPlannerEntryParams({ entryPoint, locationId, parkId });
  if (mapBounds) {
    return `?${new URLSearchParams({
      [topLeftLatParamKey]: mapBounds.topLeftLat,
      [topLeftLngParamKey]: mapBounds.topLeftLng,
      [bottomRightLatParamKey]: mapBounds.bottomRightLat,
      [bottomRightLngParamKey]: mapBounds.bottomRightLng,
      ...entryParams
    })}`;
  }
  if (mapCenter) {
    return `?${new URLSearchParams({ [latParamKey]: String(mapCenter.lat), [lngParamKey]: String(mapCenter.lng), ...entryParams })}`;
  }
  return `?${new URLSearchParams(entryParams)}`;
};

/**
 * V2 designs: https://www.figma.com/design/jRyJm5tO66lHSa0E8PSox2/H2--26-Trip-planner?node-id=2958-71169&m=dev
 */
const PlanATripCard = ({
  mapBounds,
  mapCenter,
  isNativeLink,
  variant = 'default',
  className,
  entryPoint,
  locationId,
  parkId
}: PlanATripCardProps) => {
  const isV2Enabled = useExperiments()['web-disco-rm-trip-planner-v2']?.value === 'on';
  const languageRegionCode = useLanguageRegionCode();
  const href = wrapUrlSafe(`/trip-planner${getTripPlannerQuery({ entryPoint, locationId, mapBounds, mapCenter, parkId })}`, languageRegionCode);

  const cardRef = useRef<HTMLAnchorElement>(null);
  const hasLoggedViewRef = useRef(false);
  const isVisible = useIsVisible(cardRef);

  const analyticsAttributes = useMemo(
    () => ({ entry_point: entryPoint, location_id: locationId, park_id: parkId }),
    [entryPoint, locationId, parkId]
  );

  useEffect(() => {
    if (!isVisible || hasLoggedViewRef.current) {
      return;
    }
    hasLoggedViewRef.current = true;
    logTripPlannerCTAViewed(analyticsAttributes);
  }, [analyticsAttributes, isVisible]);

  const onClick = useCallback(() => {
    logTripPlannerCTAClicked(analyticsAttributes);
  }, [analyticsAttributes]);

  return (
    <LinkOrNativeAnchor
      className={classNames(
        styles.card,
        isV2Enabled ? styles.restyled : styles.legacy,
        !isV2Enabled && variant === 'prominent' && styles.prominent,
        className
      )}
      href={href}
      onClick={onClick}
      ref={cardRef}
      {...(isNativeLink ? { isNativeLink: true } : {})}
    >
      {isV2Enabled ? (
        <Image alt="" className={styles.illustration} src={illustrationSrc} />
      ) : (
        <IconTile className={styles.icon} icon={{ Component: SparkleFilled }} radius="circle" variant="success" />
      )}
      {/* TODO: translations: https://alltrails.atlassian.net/browse/DISCO-26230 */}
      <span className={styles.text}>
        <Typography className={styles.title} component="span" variant={isV2Enabled ? 'text150bold' : 'heading200'}>
          Planning a trip?
        </Typography>
        <Typography className={styles.subtitle} component="span" variant="text100">
          {isV2Enabled ? 'Turn any destination into an unforgettable adventure' : 'Create a custom trail list with Trip Assistant'}
        </Typography>
      </span>
      {isV2Enabled ? (
        <Chevron className={styles.chevron} color="--color-text-secondary" orientation="right" size="sm" />
      ) : (
        <ArrowSq className={styles.chevron} color="--color-text-secondary" orientation="right" size="sm" />
      )}
    </LinkOrNativeAnchor>
  );
};

export default PlanATripCard;
